import React, { useEffect, useState } from "react";
import { TenantLogo } from "@/components/TenantLogo";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

const STATUS_DE = { present: "Anwesend", sick: "Krank", excused: "Entschuldigt", unexcused: "Unentschuldigt" };

export default function ParticipantSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/weiterbildung/sign/${token}`);
        setInfo(data);
      } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async () => {
    if (!signature) { toast.error("Bitte unterschreiben"); return; }
    setSaving(true);
    try {
      await api.post(`/weiterbildung/sign/${token}`, { signature });
      setDone(true);
      toast.success("Unterschrift gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-xs text-slate-500">Lädt…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="max-w-md bg-white border border-slate-200 p-8">
        <div className="text-xs font-bold uppercase tracking-overline text-red-600 mb-2">Fehler</div>
        <div className="text-sm text-slate-700">{error}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-lg bg-white border border-slate-200" data-testid="wb-sign-page">
        <div className="px-8 py-6 border-b border-slate-100">
          <div className="mb-3"><TenantLogo className="w-40 h-auto" testId="sign-page-logo" /></div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-1">Anwesenheit bestätigen</div>
          <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">{info.class_name}</div>
          <div className="text-xs text-slate-500 mt-1">{info.weiterbildung_name}</div>
        </div>
        <div className="px-8 py-6 space-y-4">
          <table className="text-sm w-full">
            <tbody>
              <tr><td className="py-2 text-slate-500 text-xs">Teilnehmer</td><td className="py-2">{info.participant_name}</td></tr>
              <tr><td className="py-2 text-slate-500 text-xs">Datum</td><td className="py-2 font-mono">{formatDeShort(info.date)}</td></tr>
              <tr><td className="py-2 text-slate-500 text-xs">Unterrichtszeit</td><td className="py-2 font-mono">{info.time_from || "?"}–{info.time_to || "?"}</td></tr>
              <tr><td className="py-2 text-slate-500 text-xs">Status</td><td className="py-2 font-medium">{STATUS_DE[info.status] || info.status}</td></tr>
            </tbody>
          </table>

          {done || info.already_signed ? (
            <div className="p-6 bg-emerald-50 border border-emerald-200 text-center">
              <div className="text-2xl mb-2">✓</div>
              <div className="font-medium text-emerald-800">Anwesenheit bereits unterschrieben</div>
              {info.signed_at && <div className="text-xs text-emerald-700 mt-1 font-mono">{new Date(info.signed_at).toLocaleString("de-DE")}</div>}
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Deine Unterschrift</label>
                <SignaturePad value={signature} onChange={setSignature} />
              </div>
              <button
                onClick={submit}
                disabled={saving || !signature}
                data-testid="wb-sign-submit"
                className="w-full bg-[#0a788c] text-white py-4 text-sm font-medium hover:bg-[#0a96b4] disabled:opacity-50"
              >
                {saving ? "Speichere…" : "Anwesenheit unterschreiben"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
